import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { find, get, merge } from 'lodash';
import { FormattedMessage, intlShape } from 'react-intl';
import LightningSolid from '../../shared-components/icons/LightningSolid';
import { Spinner } from '../../shared-components/decorators/spinner';
import { InquiryPriceBreakdownModal } from '../InquiryPriceBreakdownModal/InquiryPriceBreakdownModal';
import { formatPrice } from '../utils';
import SegmentUtil from '../../../../lib/segment_util';
import { getGAExperiments } from '../../../../lib/ga_helpers';

export const InquiryFeeCalc = ({
  quote,
  profile,
  listing,
  currency,
  isLoading,
  intl
}) => {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const totalPrice = get(quote, 'master.calculations.guest.payment', 0);

  const masterContract = get(quote, 'master', {});
  const schedule = get(masterContract, 'items[0].info.schedule', {});
  const listingPriceRange = get(listing, 'price_range', {});
  const priceFrom = get(listingPriceRange, 'min', 0);
  const priceTo = get(listingPriceRange, 'max', 0);
  const baseListingPrice = get(listing, 'prices[0].value', 0);
  const hasRange = priceFrom && priceTo;
  const minimumDuration = get(listing, 'minimum_duration', 0);
  const showAccuratePricing = !!totalPrice;

  const handleOpenModal = useCallback((e) => {
    e.preventDefault();
    setShowBreakdown(true);
  }, [showBreakdown]);

  const label = useMemo(() => {
    return showAccuratePricing
      ? intl.formatMessage({ id: 'inquiry_booking.inquiry_fee_calc.total_label', defaultMessage: 'Estimated Total' })
      : intl.formatMessage({ id: 'inquiry_booking.inquiry_fee_calc.add_details', defaultMessage: 'Add details * to view estimated price' });
  }, [showAccuratePricing, intl.locale]);

  const priceLabel = useMemo(() => {
    return showAccuratePricing
      ? formatPrice(totalPrice, currency, intl)
      : hasRange ? `${formatPrice(priceFrom, currency, intl)}–${formatPrice(priceTo, currency, intl)}` : formatPrice(baseListingPrice, currency, intl);
  }, [showAccuratePricing, intl.locale, totalPrice, priceFrom, priceTo]);

  const caption = useMemo(() => {
    return showAccuratePricing
      ? <a
        href="#"
        className="underline calc-caption"
        onClick={handleOpenModal}
      >
        {intl.formatMessage({ id: 'inquiry_booking.inquiry_fee_calc.price_breakdown_button', defaultMessage: 'View price breakdown' })}
      </a>
      : intl.formatMessage({ id: 'inquiry_booking.inquiry_fee_calc.min_hours', defaultMessage: '{hours} hr minimum' }, { hours: minimumDuration });
  }, [minimumDuration, showAccuratePricing, intl.locale, isLoading]);

  useEffect(() => {
    if (showAccuratePricing) {
      const booking = find(quote.children, { type: 'SPACE_RENTAL' });
      const contractMaster = get(quote, 'master', {});
      const calculations = get(contractMaster, 'calculations', {});

      const bookingRate = get(booking, 'meta.booking.rate', 0);
      const bookingTotal = parseFloat(get(calculations, 'guest.payment'));
      const baseListingPrice = get(listing, 'prices[0].value', 0);

      const priceFrom = get(listingPriceRange, 'min', 0);
      const priceTo = get(listingPriceRange, 'max', 0);
      const hasRange = priceFrom && priceTo;

      const showRange = !bookingRate && hasRange;
      const showTotal = !!bookingTotal;
      const showRate = bookingRate && !showTotal && !showRange;

      const priceType = showRange ? 'range' : showTotal ? 'total' : showRate ? 'hourly' : 'base rate';

      const priceQuoted = showRange
        ? listingPriceRange
        : showTotal
          ? bookingTotal
          : showRate
            ? bookingRate
            : baseListingPrice;

      const splitPaymentPermitted = get(quote, 'split_payment_permitted');
      if (window.analyticsIntegrations) {
        const editedFeeObj = SegmentUtil.parseFeeObj(get(booking, 'meta.booking'));
        const editedListing = analyticsIntegrations.normalizeListingObj(listing);

        const gaExperiments = getGAExperiments();
        const properties = merge(
          editedFeeObj,
          editedListing,
          { optimize_experiments: JSON.stringify(gaExperiments) },
          { day_count: schedule.length },
          { rate_details: get(booking, 'meta.booking.rate_details') },
          {
            split_payment: get(calculations, 'guest.split_payment'),
            split_payment_permitted: splitPaymentPermitted
          },
          {
            page: 'inquiry',
            price_type: priceType,
            price_quoted: priceQuoted
          }
        );
        analyticsIntegrations.bookingPriceCreated(properties, [
          contractMaster,
          booking
        ]);
      }
    }
  }, [showAccuratePricing, totalPrice]);

  return (
    <>
      <div className="inquiry-fee-calc full-width">
        <div>
          <div className="calc-label text-small ">
            {label}
          </div>
          <div className="calc-body flex start middle">
            {isLoading ? <Spinner additionalStyle="gray calc-spinner" /> : <>
              <LightningSolid className="pricing-icon" />
              <span className="calc-price">{priceLabel}</span>
              {
                !showAccuratePricing
                && <span className="calc-price-rate">
                  <FormattedMessage
                    id="inquiry_booking.inquiry_fee_calc.hr_rate"
                    defaultMessage="/hr"
                  />
                </span>
              }
            </>}
          </div>
        </div>
        {caption
          && <div className="calc-caption text-small">
            {caption}
          </div>
        }
      </div>
      <InquiryPriceBreakdownModal
        show={showBreakdown}
        setShow={setShowBreakdown}
        quote={quote}
        profile={profile}
        listing={listing}
        isLoading={isLoading}
        intl={intl}
      />
    </>
  );
};

InquiryFeeCalc.propTypes = {
  quote: PropTypes.object,
  profile: PropTypes.object,
  listing: PropTypes.object,
  isLoading: PropTypes.bool,
  currency: PropTypes.string,
  intl: intlShape
};
